/**
 * Inventory route factory. The shell spreads these under the authed route:
 *
 *   children: [...inventoryRoutes(authedRoute)]
 *
 * Pages stay router-free (props in, callbacks out); this file is the only
 * place inventory code touches TanStack Router. Each route is wrapped in the
 * permission boundary for the canonical permission its screen's primary
 * operation requires, so a denied page never mounts or issues a query.
 */
import {
  createRoute,
  useNavigate,
  useParams,
  useSearch,
  type AnyRoute,
} from "@tanstack/react-router";

import { useSession } from "@/lib/session/use-session";
import type { StockMovementSourceType } from "./api";
import { useCurrentBranch } from "@/lib/branch/branch-store";

import {
  INVENTORY_LOOKUP_PERMISSIONS as L,
  INVENTORY_PERMISSIONS as P,
  InventoryPermissionBoundary,
} from "./permissions";
import { WarehousesPage } from "./pages/warehouses-page";
import { StockPage } from "./pages/stock-page";
import { MovementsPage } from "./pages/movements-page";
import { MovementEntryPage } from "./pages/movement-entry-page";
import { TransfersPage } from "./pages/transfers-page";
import { TransferEntryPage } from "./pages/transfer-entry-page";
import { TransferDetailPage } from "./pages/transfer-detail-page";
import { CountsPage } from "./pages/counts-page";
import { CountEntryPage } from "./pages/count-entry-page";
import { CountReviewPage } from "./pages/count-review-page";
import { DeadStockPage } from "./pages/dead-stock-page";
import { SlowMoversPage } from "./pages/slow-movers-page";
import { VariantStatsPage } from "./pages/variant-stats-page";

export interface WarehousesSearch {
  warehouse?: string;
}

export interface StockSearch {
  sku?: string;
  variant_id?: string;
  warehouse_id?: string;
  location_id?: string;
}

export interface MovementsSearch {
  variant_id?: string;
  warehouse_id?: string;
  location_id?: string;
  transfer_id?: string;
  batch_id?: string;
  source_type?: string;
  source_id?: string;
}

export interface MovementEntrySearch {
  variant_id?: string;
  warehouse_id?: string;
}

export interface VariantStatsSearch {
  warehouse_id?: string;
}

/** Absent keys are OMITTED (never explicit undefined) per router house rules. */
function pickString(source: Record<string, unknown>, key: string): { [k: string]: string } {
  const value = source[key];
  return typeof value === "string" && value !== "" ? { [key]: value } : {};
}

function useInventoryNav() {
  const navigate = useNavigate();
  return {
    openStock: (search: StockSearch = {}) =>
      void navigate({ to: "/inventory/stock", search } as never),
    openMovements: (search: MovementsSearch = {}) =>
      void navigate({ to: "/inventory/movements", search } as never),
    newMovement: (search: MovementEntrySearch = {}) =>
      void navigate({ to: "/inventory/movements/new", search } as never),
    openTransfers: () => void navigate({ to: "/inventory/transfers" } as never),
    newTransfer: () => void navigate({ to: "/inventory/transfers/new" } as never),
    openTransfer: (transferId: string) =>
      void navigate({ to: "/inventory/transfers/$transferId", params: { transferId } } as never),
    openCounts: () => void navigate({ to: "/inventory/counts" } as never),
    openCount: (worksheetId: string) =>
      void navigate({ to: "/inventory/counts/$worksheetId", params: { worksheetId } } as never),
    openCountReview: (worksheetId: string) =>
      void navigate({
        to: "/inventory/counts/$worksheetId/review",
        params: { worksheetId },
      } as never),
    openVariantStats: (variantId: string, warehouseId?: string) =>
      void navigate({
        to: "/inventory/stock/$variantId/cover",
        params: { variantId },
        search: warehouseId === undefined ? {} : { warehouse_id: warehouseId },
      } as never),
  };
}

function WarehousesRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  const { branchId } = useCurrentBranch();
  const search = useSearch({ strict: false }) as WarehousesSearch;
  return (
    <InventoryPermissionBoundary permission={P.warehouseRead}>
      <WarehousesPage
        {...(search.warehouse === undefined ? {} : { initialWarehouseId: search.warehouse })}
        canCreate={hasPermission(P.warehouseCreate)}
        canUpdate={hasPermission(P.warehouseUpdate)}
        // Archiving reads the entity for its ETag first, so the UI action
        // needs both grants even though the API enforces only delete.
        canDelete={hasPermission(P.warehouseRead) && hasPermission(P.warehouseDelete)}
        canReadBranches={hasPermission(L.branchRead)}
        actingBranchId={branchId}
        onOpenStock={({ warehouseId, locationId }) =>
          nav.openStock({ warehouse_id: warehouseId, location_id: locationId })
        }
      />
    </InventoryPermissionBoundary>
  );
}

function StockRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  const search = useSearch({ strict: false }) as StockSearch;
  return (
    <InventoryPermissionBoundary permission={P.stockRead}>
      <StockPage
        {...(search.sku === undefined ? {} : { initialSku: search.sku })}
        {...(search.variant_id === undefined ? {} : { initialVariantId: search.variant_id })}
        {...(search.warehouse_id === undefined ? {} : { initialWarehouseId: search.warehouse_id })}
        {...(search.location_id === undefined ? {} : { initialLocationId: search.location_id })}
        canReadCategories={hasPermission(L.categoryRead)}
        canScan={hasPermission(L.variantRead)}
        canReadMovements={hasPermission(P.movementRead)}
        canCreateMovement={hasPermission(P.movementCreate)}
        canReadReports={hasPermission(P.reportRead)}
        onOpenMovements={({ variantId, warehouseId }) =>
          nav.openMovements({ variant_id: variantId, warehouse_id: warehouseId })
        }
        onNewMovement={({ variantId, warehouseId }) =>
          nav.newMovement({ variant_id: variantId, warehouse_id: warehouseId })
        }
        onOpenVariantStats={(variantId) => nav.openVariantStats(variantId)}
      />
    </InventoryPermissionBoundary>
  );
}

function VariantStatsRoute() {
  const nav = useInventoryNav();
  const params = useParams({ strict: false }) as { variantId?: string };
  const search = useSearch({ strict: false }) as VariantStatsSearch;
  return (
    <InventoryPermissionBoundary permission={P.reportRead}>
      <VariantStatsPage
        key={params.variantId}
        {...(params.variantId === undefined ? {} : { variantId: params.variantId })}
        {...(search.warehouse_id === undefined ? {} : { initialWarehouseId: search.warehouse_id })}
        onOpenStock={(variantId) => nav.openStock({ variant_id: variantId })}
        onBack={() => nav.openStock()}
      />
    </InventoryPermissionBoundary>
  );
}

function MovementsRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  const search = useSearch({ strict: false }) as MovementsSearch;
  return (
    <InventoryPermissionBoundary permission={P.movementRead}>
      <MovementsPage
        {...(search.variant_id === undefined ? {} : { initialVariantId: search.variant_id })}
        {...(search.warehouse_id === undefined ? {} : { initialWarehouseId: search.warehouse_id })}
        {...(search.location_id === undefined ? {} : { initialLocationId: search.location_id })}
        {...(search.transfer_id === undefined ? {} : { initialTransferId: search.transfer_id })}
        {...(search.batch_id === undefined ? {} : { initialBatchId: search.batch_id })}
        {...(search.source_id === undefined ? {} : { initialSourceId: search.source_id })}
        {...(search.source_type === undefined
          ? {}
          : { initialSourceType: search.source_type as StockMovementSourceType })}
        canReadTransfers={hasPermission(P.transferRead)}
        canReadStock={hasPermission(P.stockRead)}
        onOpenTransfer={nav.openTransfer}
        onOpenStock={({ variantId, warehouseId }) =>
          nav.openStock({
            variant_id: variantId,
            ...(warehouseId === "" ? {} : { warehouse_id: warehouseId }),
          })
        }
      />
    </InventoryPermissionBoundary>
  );
}

function MovementEntryRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  const search = useSearch({ strict: false }) as MovementEntrySearch;
  return (
    <InventoryPermissionBoundary permission={P.movementCreate}>
      <MovementEntryPage
        {...(search.variant_id === undefined ? {} : { initialVariantId: search.variant_id })}
        {...(search.warehouse_id === undefined ? {} : { initialWarehouseId: search.warehouse_id })}
        canReadVariants={hasPermission(L.variantRead)}
        canReadUoms={hasPermission(L.uomRead)}
        canReadBatches={hasPermission(P.batchRead)}
        canScan={hasPermission(L.variantRead)}
        onPosted={(movement) =>
          nav.openMovements({
            variant_id: movement.variant_id,
            warehouse_id: movement.warehouse_id,
          })
        }
        onCancel={() => nav.openStock()}
      />
    </InventoryPermissionBoundary>
  );
}

function TransfersRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  return (
    <InventoryPermissionBoundary permission={P.transferRead}>
      <TransfersPage
        canCreate={hasPermission(P.transferCreate)}
        onOpenTransfer={nav.openTransfer}
        onNewTransfer={nav.newTransfer}
      />
    </InventoryPermissionBoundary>
  );
}

function TransferEntryRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  return (
    <InventoryPermissionBoundary permission={P.transferCreate}>
      <TransferEntryPage
        canReadVariants={hasPermission(L.variantRead)}
        canReadUoms={hasPermission(L.uomRead)}
        canReadBatches={hasPermission(P.batchRead)}
        canScan={hasPermission(L.variantRead)}
        onCreated={nav.openTransfer}
        onOpenLegs={(transferId) => nav.openMovements({ transfer_id: transferId })}
        onCancel={nav.openTransfers}
      />
    </InventoryPermissionBoundary>
  );
}

function TransferDetailRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { transferId?: string };
  return (
    <InventoryPermissionBoundary permission={P.transferRead}>
      <TransferDetailPage
        key={params.transferId}
        {...(params.transferId === undefined ? {} : { transferId: params.transferId })}
        canUpdate={hasPermission(P.transferUpdate)}
        canShip={hasPermission(P.transferShip)}
        canReceive={hasPermission(P.transferReceive)}
        canCancel={hasPermission(P.transferCancel)}
        canReadMovements={hasPermission(P.movementRead)}
        onOpenLegs={(transferId) => nav.openMovements({ transfer_id: transferId })}
        onBack={nav.openTransfers}
      />
    </InventoryPermissionBoundary>
  );
}

function CountsRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  return (
    <InventoryPermissionBoundary permission={P.countRead}>
      <CountsPage
        canCreate={hasPermission(P.countCreate)}
        canReadCategories={hasPermission(L.categoryRead)}
        onOpenWorksheet={(worksheetId, status) => {
          // A submitted worksheet's next step is review — but only for a
          // reviewer; a counter still lands on the blind representation.
          if (status === "submitted" && hasPermission(P.countReview)) {
            nav.openCountReview(worksheetId);
            return;
          }
          nav.openCount(worksheetId);
        }}
      />
    </InventoryPermissionBoundary>
  );
}

function CountEntryRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { worksheetId?: string };
  return (
    <InventoryPermissionBoundary permission={P.countRead}>
      <CountEntryPage
        key={params.worksheetId}
        {...(params.worksheetId === undefined ? {} : { worksheetId: params.worksheetId })}
        canEnter={hasPermission(P.countEnter)}
        canSubmit={hasPermission(P.countSubmit)}
        canCancel={hasPermission(P.countCancel)}
        canReview={hasPermission(P.countReview)}
        canScan={hasPermission(L.variantRead)}
        onOpenReview={nav.openCountReview}
        onBack={nav.openCounts}
      />
    </InventoryPermissionBoundary>
  );
}

function CountReviewRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  const params = useParams({ strict: false }) as { worksheetId?: string };
  return (
    <InventoryPermissionBoundary permission={P.countReview}>
      <CountReviewPage
        key={params.worksheetId}
        {...(params.worksheetId === undefined ? {} : { worksheetId: params.worksheetId })}
        canApprove={hasPermission(P.countApprove)}
        canReadMovements={hasPermission(P.movementRead)}
        onOpenMovements={(worksheetId) =>
          nav.openMovements({ source_type: "inventory_count", source_id: worksheetId })
        }
        onBack={nav.openCounts}
      />
    </InventoryPermissionBoundary>
  );
}

function DeadStockRoute() {
  const { hasPermission } = useSession();
  return (
    <InventoryPermissionBoundary permission={P.reportRead}>
      <DeadStockPage
        canExport={hasPermission(P.reportExport)}
        canReadCategories={hasPermission(L.categoryRead)}
      />
    </InventoryPermissionBoundary>
  );
}

function SlowMoversRoute() {
  const nav = useInventoryNav();
  const { hasPermission } = useSession();
  return (
    <InventoryPermissionBoundary permission={P.reportRead}>
      <SlowMoversPage
        canReadCategories={hasPermission(L.categoryRead)}
        onOpenVariantStats={(variantId) => nav.openVariantStats(variantId)}
      />
    </InventoryPermissionBoundary>
  );
}

/**
 * Route factory per the feature-module contract. Registry names + permissions:
 *   inventory.stock            → inventory.stock.read
 *   inventory.stock.cover      → inventory.report.read
 *   inventory.movements        → inventory.movement.read
 *   inventory.movements.new    → inventory.movement.create
 *   inventory.transfers        → inventory.transfer.read
 *   inventory.transfers.new    → inventory.transfer.create
 *   inventory.transfers.detail → inventory.transfer.read
 *   inventory.counts           → inventory.count.read
 *   inventory.counts.entry     → inventory.count.read
 *   inventory.counts.review    → inventory.count.review
 *   inventory.warehouses       → inventory.warehouse.read
 *   inventory.reports.*        → inventory.report.read
 */
export function inventoryRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/stock",
      validateSearch: (search: Record<string, unknown>): StockSearch => ({
        ...pickString(search, "sku"),
        ...pickString(search, "variant_id"),
        ...pickString(search, "warehouse_id"),
        ...pickString(search, "location_id"),
      }),
      component: StockRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/stock/$variantId/cover",
      validateSearch: (search: Record<string, unknown>): VariantStatsSearch => ({
        ...pickString(search, "warehouse_id"),
      }),
      component: VariantStatsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/movements",
      validateSearch: (search: Record<string, unknown>): MovementsSearch => ({
        ...pickString(search, "variant_id"),
        ...pickString(search, "warehouse_id"),
        ...pickString(search, "location_id"),
        ...pickString(search, "transfer_id"),
        ...pickString(search, "batch_id"),
        ...pickString(search, "source_type"),
        ...pickString(search, "source_id"),
      }),
      component: MovementsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/movements/new",
      validateSearch: (search: Record<string, unknown>): MovementEntrySearch => ({
        ...pickString(search, "variant_id"),
        ...pickString(search, "warehouse_id"),
      }),
      component: MovementEntryRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/transfers",
      component: TransfersRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/transfers/new",
      component: TransferEntryRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/transfers/$transferId",
      component: TransferDetailRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/counts",
      component: CountsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/counts/$worksheetId",
      component: CountEntryRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/counts/$worksheetId/review",
      component: CountReviewRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/warehouses",
      validateSearch: (search: Record<string, unknown>): WarehousesSearch => ({
        ...pickString(search, "warehouse"),
      }),
      component: WarehousesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/reports/dead-stock",
      component: DeadStockRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "/inventory/reports/slow-movers",
      component: SlowMoversRoute,
    }),
  ] as AnyRoute[];
}
